/**
 * Solution-step utilities for the Math Question Generator.
 *
 * Contract: every template stores `solution` as newline-separated steps,
 * one reasoning step per line, plain readable math (Unicode × ÷ √ →, not
 * raw LaTeX — solutions render as text on screen AND in the PDF, where
 * KaTeX HTML is not embeddable). The question's formal notation lives in
 * the separate `latex` field.
 *
 * This parser is the single place that turns that string into steps, so
 * the worksheet UI, copy-to-clipboard, and PDF export all agree.
 */

/** Split a solution string into non-empty trimmed steps. */
export function parseSolutionSteps(solution: string | undefined): string[] {
  if (!solution) return [];
  return solution
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((s) =>
      // Tolerate templates that prefix "Step N:" / "Step N -" / "N. " —
      // strip it so the renderer owns numbering exactly once.
      s.replace(/^(?:step\s*\d+\s*[:.\-–—]\s*|\d+\s*[.)]\s*)/i, "").trim(),
    )
    .filter((s) => s.length > 0);
}

/** True when the solution teaches (multi-step) rather than restates. */
export function isMultiStepSolution(solution: string | undefined): boolean {
  return parseSolutionSteps(solution).length >= 2;
}
